import { NO_COMPANY_TEXT } from '@/lib/company-error-text';
import { ApiError, apiErrorReason } from '@/lib/api';

import type { InviteResult } from './use-provider-grants';

// Mensajes de "Otorgar acceso" (invitación de proveedores externos y alta desde el LMS).
// Toda razón conocida del backend tiene un texto accionable; nunca un slug crudo.

const ERROR_BY_REASON = new Map<string, string>([
  [
    'registered_collaborator',
    'Esta persona ya es colaboradora registrada. Búscala por su nombre y elígela de la lista.',
  ],
  [
    'lms_collaborator',
    'Esta persona ya está registrada en el LMS. Búscala por su nombre y elígela de la lista.',
  ],
  ['user_inactive', 'Este proveedor está desactivado. Reactívalo en Usuarios antes de invitarlo.'],
  [
    'idp_not_configured',
    'La invitación de proveedores aún no está habilitada en este entorno. Contacta al administrador del sistema.',
  ],
  [
    'idp_unavailable',
    'El servicio de cuentas no respondió. No se creó nada; intenta de nuevo en unos minutos.',
  ],
  [
    'lms_unavailable',
    'No se pudo verificar en el LMS si esta persona ya está registrada. No se creó nada; intenta de nuevo en unos minutos.',
  ],
  ['invite_in_progress', 'Ya hay una invitación en curso para este correo. Espera un momento.'],
  [
    'scope_out_of_company',
    'Uno de los documentos marcados ya no pertenece a la empresa activa. Cierra el formulario y vuelve a abrirlo.',
  ],
]);

export function inviteErrorMessage(e: Error): string {
  const known = ERROR_BY_REASON.get(apiErrorReason(e) ?? '');
  if (known !== undefined) {
    return known;
  }
  if (e instanceof ApiError && e.status === 400) {
    return 'Revisa los datos: el correo debe ser válido y el vencimiento, una fecha futura.';
  }
  return 'No se pudo enviar la invitación. Intenta de nuevo.';
}

export function grantErrorMessage(e: Error): string {
  const reason = apiErrorReason(e);
  if (reason === 'idp_unavailable' || reason === 'scope_out_of_company') {
    return ERROR_BY_REASON.get(reason) ?? '';
  }
  return 'No se pudo otorgar el acceso. Intenta de nuevo.';
}

export function addFromLmsErrorMessage(e: Error): string {
  const reason = apiErrorReason(e);
  if (reason === 'lms_user_conflict') {
    return 'Esta persona ya está ligada a otro usuario del QMS. Contacta al administrador del sistema.';
  }
  if (reason === 'no_active_company') {
    return NO_COMPANY_TEXT;
  }
  if (reason === 'lms_unavailable') {
    return 'El LMS no respondió. No se hizo ningún cambio; intenta de nuevo en unos minutos.';
  }
  if (e instanceof ApiError && e.status === 404) {
    return 'Esa persona ya no figura como activa en la empresa en el LMS. Búscala de nuevo.';
  }
  return 'No se pudo preparar el usuario de esa persona. Intenta de nuevo.';
}

/** Aviso tras invitar: [mensaje, tono?]. */
export function inviteSuccessToast(r: InviteResult, email: string): [string, 'error'?] {
  if (r.invitation === 'failed') {
    return [
      `Acceso creado, pero el correo a ${email} no salió. Usa "Reenviar invitación".`,
      'error',
    ];
  }
  return r.account === 'existing'
    ? [`Nuevo acceso otorgado a ${email}. Se le avisó por correo.`]
    : [`Invitación enviada a ${email}`];
}
